import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  CHAT_ATTACHMENT_DOWNLOAD_ENDPOINT,
  CHAT_ATTACHMENT_UPLOAD_ENDPOINT,
  CHAT_ATTACH_BUSINESS_DOCUMENT_ENDPOINT,
  CHAT_BUSINESS_DOCUMENT_CONVERSATION_ENDPOINT,
  CHAT_BUSINESS_DOCUMENT_CONVERSATIONS_ENDPOINT,
  CHAT_CONVERSATION_ENDPOINT,
  CHAT_CONVERSATIONS_ENDPOINT,
  CHAT_MESSAGES_ENDPOINT,
  CHAT_MESSAGE_DELIVERED_ENDPOINT,
  CHAT_READ_ENDPOINT,
  CHAT_UNREAD_COUNT_ENDPOINT,
  CHAT_USERS_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';

export interface ChatUser {
  id: string;
  display_name: string;
  email: string;
  profile_image_key: string | null;
}

export interface ChatParticipant {
  user: ChatUser;
  joined_at: string;
  last_read_at: string | null;
  muted: boolean;
}

export interface ChatAttachment {
  id: string;
  object_key: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
}

export interface ChatMessageReceipt {
  user_id: string;
  delivered_at: string | null;
  read_at: string | null;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender: ChatUser;
  reply_to_message_id: string | null;
  client_message_id: string | null;
  content: string | null;
  edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
  attachments: ChatAttachment[];
  receipts: ChatMessageReceipt[];
}

export interface ChatConversation {
  id: string;
  organisation_id: string | null;
  conversation_type: 'direct' | 'group';
  title: string | null;
  business_document_id: string | null;
  business_document_route: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  participants: ChatParticipant[];
  last_message: ChatMessage | null;
  unread_count: number;
}

export interface BusinessDocumentChatReference {
  id: string;
  conversation_type: 'direct' | 'group';
  title: string | null;
  business_document_route: string | null;
  participant_count: number;
  can_read_messages: boolean;
  unread_count: number;
  updated_at: string;
}

export interface ChatPage<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

interface ChatApiPage<T> {
  items?: T[];
  records?: T[];
  total?: number;
  total_records?: number;
  page?: number;
  per_page?: number;
  limit?: number;
}

export interface PendingChatAttachment {
  object_key: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  constructor(private readonly restApi: RestApiService) {}

  async searchUsers(query = '', limit = 25): Promise<ChatUser[]> {
    const params = new HttpParams().set('query', query).set('limit', limit);
    const response = await firstValueFrom(
      this.restApi.get<ChatUser[] | ChatApiPage<ChatUser>>(CHAT_USERS_ENDPOINT, { params }),
    );
    return Array.isArray(response) ? response : response.records || response.items || [];
  }

  async listConversations(page = 1, perPage = 25): Promise<ChatPage<ChatConversation>> {
    const params = new HttpParams().set('page', page).set('per_page', perPage);
    const response = await firstValueFrom(
      this.restApi.get<ChatApiPage<ChatConversation>>(CHAT_CONVERSATIONS_ENDPOINT, { params }),
    );
    return this.normalizePage(response, page, perPage);
  }

  getConversation(conversationId: string): Promise<ChatConversation> {
    return firstValueFrom(
      this.restApi.get<ChatConversation>(CHAT_CONVERSATION_ENDPOINT(conversationId)),
    );
  }

  getBusinessDocumentConversation(businessDocumentId: string): Promise<ChatConversation> {
    return firstValueFrom(
      this.restApi.get<ChatConversation>(
        CHAT_BUSINESS_DOCUMENT_CONVERSATION_ENDPOINT(businessDocumentId),
      ),
    );
  }

  listBusinessDocumentConversations(businessDocumentId: string): Promise<BusinessDocumentChatReference[]> {
    return firstValueFrom(
      this.restApi.get<BusinessDocumentChatReference[]>(
        CHAT_BUSINESS_DOCUMENT_CONVERSATIONS_ENDPOINT(businessDocumentId),
      ),
    );
  }

  attachBusinessDocument(
    conversationId: string,
    businessDocumentId: string,
    businessDocumentRoute: string,
  ): Promise<ChatConversation> {
    return firstValueFrom(
      this.restApi.post<ChatConversation>(CHAT_ATTACH_BUSINESS_DOCUMENT_ENDPOINT(conversationId), {
        business_document_id: businessDocumentId,
        business_document_route: businessDocumentRoute,
      }),
    );
  }

  createDirectConversation(userId: string): Promise<ChatConversation> {
    return firstValueFrom(
      this.restApi.post<ChatConversation>(CHAT_CONVERSATIONS_ENDPOINT, {
        conversation_type: 'direct',
        participant_user_ids: [userId],
      }),
    );
  }

  createGroupConversation(title: string, userIds: string[]): Promise<ChatConversation> {
    return firstValueFrom(
      this.restApi.post<ChatConversation>(CHAT_CONVERSATIONS_ENDPOINT, {
        conversation_type: 'group',
        title,
        participant_user_ids: userIds,
      }),
    );
  }

  createBusinessDocumentGroupConversation(
    title: string,
    userIds: string[],
    businessDocumentId: string,
    businessDocumentRoute: string,
  ): Promise<ChatConversation> {
    return firstValueFrom(
      this.restApi.post<ChatConversation>(CHAT_CONVERSATIONS_ENDPOINT, {
        conversation_type: 'group',
        title,
        participant_user_ids: userIds,
        business_document_id: businessDocumentId,
        business_document_route: businessDocumentRoute,
      }),
    );
  }

  async listMessages(conversationId: string, page = 1, perPage = 100): Promise<ChatPage<ChatMessage>> {
    const params = new HttpParams().set('page', page).set('per_page', perPage);
    const response = await firstValueFrom(
      this.restApi.get<ChatApiPage<ChatMessage>>(CHAT_MESSAGES_ENDPOINT(conversationId), { params }),
    );
    return this.normalizePage(response, page, perPage);
  }

  sendMessage(
    conversationId: string,
    content: string,
    attachments: PendingChatAttachment[] = [],
    clientMessageId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
  ): Promise<ChatMessage> {
    return firstValueFrom(
      this.restApi.post<ChatMessage>(CHAT_MESSAGES_ENDPOINT(conversationId), {
        content,
        client_message_id: clientMessageId,
        attachments,
      }),
    );
  }

  async uploadAttachment(file: File): Promise<PendingChatAttachment> {
    const presigned = await firstValueFrom(
      this.restApi.post<{ upload_url: string; object_key: string }>(CHAT_ATTACHMENT_UPLOAD_ENDPOINT, {
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
      }),
    );
    const response = await fetch(presigned.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!response.ok) {
      throw new Error('Chat attachment upload failed.');
    }
    return {
      object_key: presigned.object_key,
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
    };
  }

  markDelivered(conversationId: string, messageId: string): Promise<void> {
    return firstValueFrom(this.restApi.post<void>(CHAT_MESSAGE_DELIVERED_ENDPOINT(conversationId, messageId), {}));
  }

  markRead(conversationId: string): Promise<void> {
    return firstValueFrom(this.restApi.post<void>(CHAT_READ_ENDPOINT(conversationId), {}));
  }

  async unreadCount(): Promise<number> {
    const response = await firstValueFrom(
      this.restApi.get<{ unread_count: number }>(CHAT_UNREAD_COUNT_ENDPOINT),
    );
    return Math.max(0, Number(response.unread_count || 0));
  }

  async attachmentDownloadUrl(attachmentId: string): Promise<string> {
    const response = await firstValueFrom(
      this.restApi.get<{ download_url: string }>(CHAT_ATTACHMENT_DOWNLOAD_ENDPOINT(attachmentId)),
    );
    return response.download_url;
  }

  private normalizePage<T>(response: ChatApiPage<T>, page: number, perPage: number): ChatPage<T> {
    return {
      items: response.records || response.items || [],
      total: Number(response.total_records ?? response.total ?? 0),
      page: Number(response.page ?? page),
      per_page: Number(response.per_page ?? response.limit ?? perPage),
    };
  }
}
